import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Modal from 'react-modal';
import { testimonialsApi } from '../../../api/adminApi';
import './TestimonialsList.css';

const DEFAULT_LIMIT = 10;

function boolToSelectValue(val) {
  if (val === true || val === 'true' || val === 1 || val === '1') return 'true';
  if (val === false || val === 'false' || val === 0 || val === '0') return 'false';
  return '';
}

function TestimonialsList() {
  const location = useLocation();
  const navigate = useNavigate();
  const [testimonials, setTestimonials] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(DEFAULT_LIMIT);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Form modal (shared for add & edit)
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [formName, setFormName] = useState('');
  const [formReview, setFormReview] = useState('');
  const [formRating, setFormRating] = useState('5');
  const [formIsFeatured, setFormIsFeatured] = useState('');
  const [formIsVerified, setFormIsVerified] = useState('');
  const [formAvatar, setFormAvatar] = useState(null);
  const [formPhoto, setFormPhoto] = useState(null);
  const [formVideo, setFormVideo] = useState(null);
  const [formSaving, setFormSaving] = useState(false);
  const [formError, setFormError] = useState('');

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadTestimonials = (pageNum = page) => {
    setLoading(true);
    setError('');
    testimonialsApi
      .list(pageNum, limit)
      .then((res) => {
        setTestimonials(res.list);
        setTotal(res.total);
        setPage(res.page);
      })
      .catch((err) => setError(err.userMessage || 'Failed to load testimonials'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadTestimonials(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Open edit modal when navigated back from the detail view with state
  useEffect(() => {
    const editTestimonial = location.state?.editTestimonial;
    if (editTestimonial) {
      navigate(location.pathname, { replace: true, state: {} });
      openEditModal(editTestimonial);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.editTestimonial]);

  const openAddModal = () => {
    setEditingTestimonial(null);
    setFormName('');
    setFormReview('');
    setFormRating('5');
    setFormIsFeatured('');
    setFormIsVerified('');
    setFormAvatar(null);
    setFormPhoto(null);
    setFormVideo(null);
    setFormError('');
    setFormModalOpen(true);
  };

  const openEditModal = (t) => {
    setEditingTestimonial(t);
    setFormName(t.name ?? '');
    setFormReview(t.review ?? '');
    setFormRating(t.rating != null ? String(t.rating) : '5');
    setFormIsFeatured(boolToSelectValue(t.is_featured));
    setFormIsVerified(boolToSelectValue(t.is_verified));
    setFormAvatar(null);
    setFormPhoto(null);
    setFormVideo(null);
    setFormError('');
    setFormModalOpen(true);
  };

  const closeFormModal = () => {
    setFormModalOpen(false);
    setEditingTestimonial(null);
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    setFormError('');

    if (!formName.trim()) {
      setFormError('Name is required.');
      return;
    }
    if (!formReview.trim()) {
      setFormError('Review is required.');
      return;
    }
    const ratingNum = Number(formRating);
    if (!formRating || Number.isNaN(ratingNum) || ratingNum < 1 || ratingNum > 5) {
      setFormError('Rating must be a number between 1 and 5.');
      return;
    }

    const hasNewAvatarOrPhoto = formAvatar instanceof File || formPhoto instanceof File;
    const hasExistingAvatarOrPhoto =
      editingTestimonial &&
      (Boolean(editingTestimonial.avatar_url) || Boolean(editingTestimonial.photo_url));
    if (!editingTestimonial && !hasNewAvatarOrPhoto) {
      setFormError('Please upload an avatar or a photo — at least one image is required.');
      return;
    }
    if (editingTestimonial && !hasExistingAvatarOrPhoto && !hasNewAvatarOrPhoto) {
      setFormError('Please upload an avatar or a photo — at least one image is required.');
      return;
    }

    const fields = {
      name: formName.trim(),
      review: formReview.trim(),
      rating: ratingNum,
      is_featured: formIsFeatured,
      is_verified: formIsVerified,
      avatar: formAvatar,
      photo: formPhoto,
      video: formVideo,
    };

    setFormSaving(true);
    const promise = editingTestimonial
      ? testimonialsApi.update(editingTestimonial.id, fields)
      : testimonialsApi.create(fields);

    promise
      .then(() => {
        closeFormModal();
        loadTestimonials(page);
      })
      .catch((err) => setFormError(err.userMessage || 'Something went wrong.'))
      .finally(() => setFormSaving(false));
  };

  const handleDeleteClick = (t) => setDeleteTarget(t);
  const handleDeleteCancel = () => setDeleteTarget(null);
  const handleDeleteConfirm = () => {
    if (!deleteTarget) return;
    setDeleting(true);
    testimonialsApi
      .delete(deleteTarget.id)
      .then(() => {
        setDeleteTarget(null);
        loadTestimonials(page);
      })
      .catch((err) => setError(err.userMessage || 'Failed to delete'))
      .finally(() => setDeleting(false));
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  const renderStars = (rating) => {
    const r = Math.min(5, Math.max(0, Number(rating) || 0));
    return (
      <span className="admin-testimonials-stars" aria-label={`${r} out of 5 stars`}>
        {'★'.repeat(r)}
        <span className="admin-testimonials-stars-empty">{'★'.repeat(5 - r)}</span>
      </span>
    );
  };

  const isEditing = editingTestimonial !== null;

  return (
    <div className="admin-testimonials">
      <div className="admin-testimonials-header">
        <h2>Testimonials</h2>
        <button type="button" className="admin-testimonials-add" onClick={openAddModal}>
          Add testimonial
        </button>
      </div>

      {error && (
        <div className="admin-testimonials-error" role="alert">
          {error}
        </div>
      )}

      {loading ? (
        <div className="admin-testimonials-loading">Loading testimonials…</div>
      ) : (
        <div className="admin-testimonials-table-wrap">
          <table className="admin-testimonials-table">
            <thead>
              <tr>
                <th>Avatar</th>
                <th>Name</th>
                <th>Rating</th>
                <th>Review</th>
                <th>Featured</th>
                <th>Verified</th>
                <th>Added</th>
                <th className="admin-testimonials-actions-col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {testimonials.length === 0 ? (
                <tr>
                  <td colSpan={8} className="admin-testimonials-empty">
                    No testimonials yet.{' '}
                    <button type="button" className="admin-testimonials-empty-btn" onClick={openAddModal}>
                      Add one
                    </button>.
                  </td>
                </tr>
              ) : (
                testimonials.map((t) => (
                  <tr key={t.id}>
                    <td>
                      {t.avatar_url ? (
                        <img
                          src={t.avatar_url}
                          alt={t.name}
                          className="admin-testimonials-avatar"
                        />
                      ) : (
                        <div className="admin-testimonials-avatar-placeholder">
                          {(t.name || '?').charAt(0).toUpperCase()}
                        </div>
                      )}
                    </td>
                    <td className="admin-testimonials-name-cell">
                      <div>{t.name || '—'}</div>
                      {t.creator?.email && (
                        <div className="admin-testimonials-creator">{t.creator.email}</div>
                      )}
                    </td>
                    <td>{renderStars(t.rating)}</td>
                    <td className="admin-testimonials-review-cell">
                      {(t.review || '').slice(0, 80)}
                      {(t.review || '').length > 80 ? '…' : ''}
                    </td>
                    <td>
                      <span className={`admin-testimonials-badge ${t.is_featured ? 'badge-yes' : 'badge-no'}`}>
                        {t.is_featured ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td>
                      <span className={`admin-testimonials-badge ${t.is_verified ? 'badge-yes' : 'badge-no'}`}>
                        {t.is_verified ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td className="admin-testimonials-date-cell">
                      {t.created_at
                        ? new Date(t.created_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })
                        : '—'}
                    </td>
                    <td className="admin-testimonials-actions-col">
                      <Link
                        to={`/admin/testimonials/${t.id}`}
                        className="admin-testimonials-btn admin-testimonials-btn-view"
                      >
                        View
                      </Link>
                      <button
                        type="button"
                        className="admin-testimonials-btn admin-testimonials-btn-edit"
                        onClick={() => openEditModal(t)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="admin-testimonials-btn admin-testimonials-btn-delete"
                        onClick={() => handleDeleteClick(t)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {!loading && total > 0 && totalPages > 1 && (
        <div className="admin-testimonials-pagination">
          <button
            type="button"
            className="admin-testimonials-page-btn"
            onClick={() => loadTestimonials(page - 1)}
            disabled={page <= 1}
          >
            Previous
          </button>
          <span className="admin-testimonials-page-info">
            Page {page} of {totalPages} ({total} total)
          </span>
          <button
            type="button"
            className="admin-testimonials-page-btn"
            onClick={() => loadTestimonials(page + 1)}
            disabled={page >= totalPages}
          >
            Next
          </button>
        </div>
      )}

      {/* Add / Edit Testimonial Modal */}
      <Modal
        isOpen={formModalOpen}
        onRequestClose={closeFormModal}
        contentLabel={isEditing ? 'Edit testimonial' : 'Add testimonial'}
        className="admin-testimonials-modal-content"
        overlayClassName="admin-testimonials-modal-overlay"
      >
        <div className="admin-testimonials-modal-inner">
          <h3>{isEditing ? 'Edit testimonial' : 'Add testimonial'}</h3>
          <form onSubmit={handleFormSubmit}>
            {formError && (
              <div className="admin-testimonials-form-error" role="alert">
                {formError}
              </div>
            )}

            <div className="admin-testimonials-form-field">
              <label htmlFor="form-t-name">Name *</label>
              <input
                id="form-t-name"
                type="text"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="e.g. Jane Smith"
                disabled={formSaving}
              />
            </div>

            <div className="admin-testimonials-form-field">
              <label htmlFor="form-t-review">Review *</label>
              <textarea
                id="form-t-review"
                value={formReview}
                onChange={(e) => setFormReview(e.target.value)}
                placeholder="Write the testimonial review…"
                rows={3}
                disabled={formSaving}
              />
            </div>

            <div className="admin-testimonials-form-row">
              <div className="admin-testimonials-form-field">
                <label htmlFor="form-t-rating">Rating * (1–5)</label>
                <input
                  id="form-t-rating"
                  type="number"
                  min={1}
                  max={5}
                  value={formRating}
                  onChange={(e) => setFormRating(e.target.value)}
                  disabled={formSaving}
                />
              </div>
              <div className="admin-testimonials-form-field">
                <label htmlFor="form-t-featured">Featured</label>
                <select
                  id="form-t-featured"
                  value={formIsFeatured}
                  onChange={(e) => setFormIsFeatured(e.target.value)}
                  disabled={formSaving}
                >
                  <option value="">— not set —</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div>
              <div className="admin-testimonials-form-field">
                <label htmlFor="form-t-verified">Verified</label>
                <select
                  id="form-t-verified"
                  value={formIsVerified}
                  onChange={(e) => setFormIsVerified(e.target.value)}
                  disabled={formSaving}
                >
                  <option value="">— not set —</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div>
            </div>

            <p className="admin-testimonials-media-required-hint">
              <strong>Avatar or photo is required</strong> — upload at least one image (you may add both).
            </p>

            {/* Avatar */}
            <div className="admin-testimonials-form-field">
              <label htmlFor="form-t-avatar">Avatar (image)</label>
              {isEditing && editingTestimonial.avatar_url && !formAvatar && (
                <div className="admin-testimonials-media-preview">
                  <img src={editingTestimonial.avatar_url} alt="Current avatar" />
                  <span className="admin-testimonials-media-hint">Current — upload to replace</span>
                </div>
              )}
              <input
                id="form-t-avatar"
                type="file"
                accept="image/*"
                onChange={(e) => setFormAvatar(e.target.files?.[0] || null)}
                disabled={formSaving}
              />
            </div>

            {/* Photo */}
            <div className="admin-testimonials-form-field">
              <label htmlFor="form-t-photo">Photo (image)</label>
              {isEditing && editingTestimonial.photo_url && !formPhoto && (
                <div className="admin-testimonials-media-preview">
                  <img src={editingTestimonial.photo_url} alt="Current photo" />
                  <span className="admin-testimonials-media-hint">Current — upload to replace</span>
                </div>
              )}
              <input
                id="form-t-photo"
                type="file"
                accept="image/*"
                onChange={(e) => setFormPhoto(e.target.files?.[0] || null)}
                disabled={formSaving}
              />
            </div>

            {/* Video */}
            <div className="admin-testimonials-form-field">
              <label htmlFor="form-t-video">Video</label>
              {isEditing && editingTestimonial.video_url && !formVideo && (
                <div className="admin-testimonials-media-preview admin-testimonials-media-preview--video">
                  <video src={editingTestimonial.video_url} controls />
                  <span className="admin-testimonials-media-hint">Current — upload to replace</span>
                </div>
              )}
              <input
                id="form-t-video"
                type="file"
                accept="video/*"
                onChange={(e) => setFormVideo(e.target.files?.[0] || null)}
                disabled={formSaving}
              />
            </div>

            <div className="admin-testimonials-form-actions">
              <button
                type="button"
                className="admin-testimonials-form-cancel"
                onClick={closeFormModal}
                disabled={formSaving}
              >
                Cancel
              </button>
              <button type="submit" className="admin-testimonials-form-submit" disabled={formSaving}>
                {formSaving
                  ? isEditing ? 'Updating…' : 'Creating…'
                  : isEditing ? 'Update' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      </Modal>

      {/* Delete Confirm Dialog */}
      {deleteTarget && (
        <div className="admin-testimonials-delete-modal" role="dialog" aria-modal="true" aria-labelledby="delete-t-title">
          <div className="admin-testimonials-delete-backdrop" onClick={handleDeleteCancel} aria-hidden="true" />
          <div className="admin-testimonials-delete-box">
            <h3 id="delete-t-title">Delete testimonial?</h3>
            <p>
              This will permanently delete the testimonial by &ldquo;{deleteTarget.name}&rdquo;. This cannot be undone.
            </p>
            <div className="admin-testimonials-delete-actions">
              <button
                type="button"
                className="admin-testimonials-btn admin-testimonials-btn-cancel"
                onClick={handleDeleteCancel}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="admin-testimonials-btn admin-testimonials-btn-delete"
                onClick={handleDeleteConfirm}
                disabled={deleting}
              >
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TestimonialsList;
